<template>
    <AppBase>
        <div class="home" @scroll="scroll" ref="home">
            <v-fade-transition>
                <v-overlay v-if="!isPreloaded">
                    <v-progress-circular indeterminate size="64"></v-progress-circular>
                </v-overlay>
                <div v-else>
                    <div class="parallax">
                        <div class="parallax-background" :class="{ night: isNight }"></div>
                        <div class="parallax-skyline" :style="{ left: `${skyline}px` }"></div>
                        <div class="parallax-foreground" :style="{ left: `${foreground}px` }"></div>
                        <div class="parallax-lamps" :style="{ left: `${lamps}px` }"></div>
                    </div>
                    <div class="modules" :style="cssVars">
                        <div class="module" :style="cssVars" v-for="module in modules" :key="module.id" :class="{ disabled: isDisabled(module) }" v-ripple @click="go(module)">
                            <span class="module-id">{{ module.id }}</span>
                            <span class="module-title">{{ module.title }}</span>
                            <v-overlay absolute class="mt-n3 mb-n6" :value="isCompleted(module)">
                                <v-icon size="96">mdi-check</v-icon>
                            </v-overlay>
                        </div>
                    </div>
                    <div class="module-labels" :style="cssVars">
                        <div class="intro" :style="cssVars">PEEPScapade 1: Intro👋🏾👋🏾</div>
                        <div class="realtalk" :style="cssVars">PEEPScapade 2: #realtalk</div>
                        <div class="woke" :style="cssVars">PEEPScapade 3: Woke Up Like This</div>
                        <div class="lit" :style="cssVars">PEEPScapade 4: Making Tough Situations LITuations🔥🔥</div>
                    </div>
                </div>
            </v-fade-transition>
            <ModalFeedback class="popup" ref="modalFeedback"></ModalFeedback>
        </div>
    </AppBase>
</template>

<script>
    import AppBase from '@/components/AppBase';
    import { get } from 'vuex-pathify';
    import ease from 'easy-ease';
    import { findIndex, map, difference } from 'lodash';

    export default {
        name: 'Home',
        components: { AppBase },
        data: () => ({
            window: {
                width: window.innerWidth,
                height: window.innerHeight
            },
            isPreloaded: false,
            isNight: false,
            skyline: 0,
            foreground: 0,
            lamps: 0
        }),
        computed: {
            modules: get('content@modules'),
            completed: get('completed'),
            allCompleted: get('allComplete'),
            lastCompletedModuleId() {
                const completed = this.$store.get('completed');
                if (completed.length) {
                    return completed[completed.length - 1].id;
                }
                return 0;
            },
            firstUncompletedModuleId() {
                if (this.completed.length) {
                    let uncompleted = difference(map(this.modules, 'id'), map(this.completed, 'id')).sort((a, b) => a - b);

                    if (uncompleted.length) {
                        return uncompleted[0];
                    } else {
                        return this.modules[this.modules.length - 1].id;
                    }
                }
                return 1;
            },
            cssVars() {
                return {
                    '--modules-width': this.window.width * this.modules.length + 'px',
                    '--module-width': this.window.width + 'px',
                    '--window-height': this.window.height + 'px'
                };
            }
        },
        methods: {
            isCompleted(module) {
                return findIndex(this.completed, { id: module.id }) != -1;
            },
            isDisabled(module) {
                return this.isCompleted(module);
            },
            onResize() {
                this.window.width = window.innerWidth;
                this.window.height = window.innerHeight;
            },
            scroll(e) {
                const left = e.target.scrollLeft;
                this.isNight = 3200 > left && left > 2000;
                this.skyline = left * -0.3;
                this.foreground = left * -0.85;
                this.lamps = left * -1.2;
            },
            go(module) {
                if (module.id == this.firstUncompletedModuleId || this.allCompleted) {
                    this.$router.push(`/m/${module.id}/1`);
                } else {
                    if (this.isCompleted(module)) {
                        this.$refs.modalFeedback.show(false, `You must complete all modules before repeating a prior module.`);
                    } else {
                        this.$refs.modalFeedback.show(false, `You must complete all prior modules before visiting this module.`);
                    }
                }
            }
        },
        mounted() {
            const preload = {
                background: new Image(),
                skyline: new Image(),
                foreground: new Image(),
                lamps: new Image()
            };
            preload.background.src = require('@/assets/map/background.png');
            preload.skyline.src = require('@/assets/map/skyline.png');
            preload.foreground.src = require('@/assets/map/foreground.png');
            preload.lamps.src = require('@/assets/map/lamps.png');
            Promise.all([
                new Promise((resolve) => {
                    preload.background.onload = resolve;
                }),
                new Promise((resolve) => {
                    preload.skyline.onload = resolve;
                }),
                new Promise((resolve) => {
                    preload.foreground.onload = resolve;
                }),
                new Promise((resolve) => {
                    preload.lamps.onload = resolve;
                })
            ]).then(() => {
                this.isPreloaded = true;
            });

            this.$nextTick(() => {
                window.addEventListener('resize', this.onResize);
            });

            const left = (this.firstUncompletedModuleId - 1) * this.window.width;

            ease({
                startValue: 0,
                endValue: left,
                durationMs: 1000,
                onStep: (value) => {
                    if (this.$refs.home) {
                        this.$refs.home.scrollLeft = value;
                    }
                }
            });
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
        }
    };
</script>

<style lang="scss" scoped>
    .home {
        margin-top: -56px;
        position: relative;
        width: 100vw;
        height: 100vh;
        overflow-x: scroll;
        background-color: #111;
    }
    .parallax {
        height: 100vh;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        min-width: 100%;
        .parallax-background {
            &.night {
                background-color: #0e1432;
            }
            background: #add8e6 url('~@/assets/map/background.png') no-repeat 0 0;
            background-size: cover;
            transition: background-color 1s;
            height: 100vh;
            position: fixed;
            top: 0;
            bottom: 0;
            width: 100vw;
        }
        .parallax-skyline {
            background: url('~@/assets/map/skyline.png') no-repeat 0 100%;
            background-size: contain;
            height: 50vh;
            width: 6870px;
            bottom: 300px;
            position: absolute;
        }
        .parallax-foreground {
            background: url('~@/assets/map/foreground.png') no-repeat 0 100%;
            background-size: contain;
            height: 600px;
            width: 11400px;
            bottom: 0;
            position: absolute;
            z-index: 2;
        }
        .parallax-lamps {
            background: url('~@/assets/map/lamps.png') no-repeat;
            background-size: contain;
            height: 40vh;
            width: 11000px;
            bottom: 0;
            position: absolute;
            z-index: 3;
        }
    }
    .modules {
        position: absolute;
        top: 0;
        left: 0;
        padding-top: 45vh;
        height: 100%;
        box-sizing: border-box;
        width: var(--modules-width);
        overflow-x: scroll;
        z-index: 999;
        .module {
            position: relative;
            z-index: 99;
            width: var(--module-width);
            display: flex;
            align-items: center;
            flex-direction: column;
            align-content: center;
            justify-content: space-around;
            float: left;
            .module-id,
            .module-title {
                color: #111 !important;
                border-color: #111 !important;
                background: #fff;
                font-weight: 700;
                justify-content: center;
                display: flex;
                align-items: center;
                box-sizing: border-box;
            }
            .module-id {
                margin: 0.5em 0;
                padding: 0.5em;
                border-radius: 50%;
                border: 4px solid #aaa;
                font-size: 2.5em;
                width: 100px;
                height: 100px;
            }
            .module-title {
                padding: 1em;
                border-radius: 5px;
                border: 4px solid #aaa;
                font-size: 1.2em;
                line-height: 1.2em;
                text-align: center;
                width: 300px;
            }
            &.disabled {
                opacity: 0.5;
            }
            &:before {
                content: '';
                height: 4px;
                background: #111 !important;
                width: var(--module-width);
                left: 50%;
                position: absolute;
                z-index: -1;
                transform: rotate(7deg);
                top: 35%;
            }
            &:hover {
                color: #111 !important;
                border-color: #111 !important;
            }
            &:nth-child(2n) {
                margin-top: 2em;
                &:before {
                    top: 20%;
                    transform: rotate(-7deg);
                }
            }
            &:last-child:before {
                content: none;
            }
        }
    }
    .module-labels {
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 100;
        width: var(--modules-width);
        div {
            background: linear-gradient(90deg, #2e343c, rgba(46, 52, 60, 0));
            color: #fff;
            font-size: 1.2em;
            display: inline-block;
            padding: 0.75em;
            position: relative;
            &:before {
                background: #2e343c;
                bottom: 0;
                content: '';
                display: block;
                height: 100vh;
                left: 0;
                position: absolute;
                width: 3px;
            }
        }
        .intro {
            width: calc(var(--module-width) * 4);
        }
        .realtalk {
            width: calc(var(--module-width) * 6);
        }
        .woke {
            width: calc(var(--module-width) * 5);
        }
    }
    .popup {
        z-index: 1000;
    }
</style>
